<template>
    <div class="page__sidebar">
        <header class="page__sidebar__header">
            <router-link to="/" class="page__sidebar__logo">
                <img
                    v-if="
                        userData.organization !== null &&
                            typeof userData.organization !== 'undefined' &&
                            typeof userData.organization.professional !== 'undefined' &&
                            parseInt(userData.organization.professional) > 0
                    "
                    class="page__sidebar__logo__image logo-pro"
                    src="@/images/connect-pro.svg"
                    alt
                />
                <img v-else class="page__sidebar__logo__image logo-normal" src="@/images/connect.svg" alt />
            </router-link>

            <div class="page__sidebar__nav-toggle" :class="{ 'page__sidebar__nav-toggle--open': mobileNavOpen }" @click="mobileNavOpen = !mobileNavOpen">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </header>

        <div class="page__sidebar__main" :class="{ 'page__sidebar__main--open': mobileNavOpen }">
            <nav class="nav nav--primary">
                <ul class="nav__list">
                    <!-- Account -->
                    <li class="nav__item">
                        <router-link to="/account" active-class="active" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-user"></i>
                            </span>

                            <span class="nav__link__text">{{ userData.name }}</span>
                        </router-link>
                    </li>

                    <!-- Invitations -->
                    <li v-if="invitations.length > 0" class="nav__item">
                        <div class="nav--invitations">
                            <a
                                class="nav__link nav__link--badgy clickable"
                                :class="{
                                    active: showInvitations || isRoutePathActive('/systems/(.*)?/invitations') || isRoutePathActive('/organizations/invitations/(.*)?'),
                                }"
                                @click="showInvitations = !showInvitations"
                            >
                                <span class="nav__link__illustration">
                                    <i class="icon icon--common-info"></i>
                                </span>

                                <span class="nav__link__text">{{ $t('navigation.invitations') }}</span>
                                <span class="nav__badge">{{ invitations.length }}</span>
                            </a>

                            <slide-down-up-transition>
                                <ul v-if="showInvitations" class="nav__list">
                                    <li v-for="invitation in invitations" :key="invitation.id" class="nav__item">
                                        <router-link
                                            :to="{
                                                name: invitation.system ? 'system-invitations' : 'organization-invitations',
                                                params: {
                                                    id: invitation.system ? invitation.system.id : false,
                                                    invitationId: invitation.id,
                                                },
                                            }"
                                            @click.native="showInvitations = false"
                                            class="nav__link clickable"
                                        >
                                            <span class="nav__link__text">
                                                <strong v-if="invitation.system">{{ invitation.system.name || 'Eigentümereinladung' }}</strong>
                                                <strong v-else>
                                                    {{ $t('invitation.organization_invitation') }}
                                                </strong>
                                                <br />
                                                <span class="small" v-if="invitation.invitee">
                                                    {{
                                                        $t('navigation.invitation_from', {
                                                            invitee: invitation.invitee.name,
                                                        })
                                                    }}
                                                </span>
                                                <span class="small" v-if="invitation.invited_organization">
                                                    {{
                                                        $t('navigation.invitation_from', {
                                                            invitee: invitation.invited_organization.name,
                                                        })
                                                    }}
                                                </span>
                                            </span>
                                        </router-link>
                                    </li>
                                </ul>
                            </slide-down-up-transition>
                        </div>
                    </li>

                    <!-- Tech: activate system -->
                    <li v-if="$can('activate', 'System') && userData.organization.role !== 'person'" class="nav__item nav-activate-system">
                        <a @click="showActivationDialog()" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-system"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('activation.activate_new_system') }}</span>
                        </a>
                    </li>

                    <!-- Systems -->
                    <li class="nav__item">
                        <router-link
                            to="/systems"
                            :class="{
                                active:
                                    isRoutePathActive('/systems', $can('see Extended Navigation', activeSystem)) && !isRoutePathActive('/systems/(.*)?/invitations') && !isRoutePathActive('/user/.+'),
                            }"
                            class="nav__link clickable"
                        >
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-systems"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('navigation.my_systems') }}</span>
                        </router-link>
                    </li>

                    <!-- Systems Admin -->
                    <li class="nav__item nav-sysadmin" v-if="$can('see', 'System overview')">
                        <router-link to="/systemoverview" active-class="active" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-systems"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('systems.overview') }}</span>
                        </router-link>
                    </li>

                    <!-- System Details -->
                    <li class="nav__item" v-if="activeSystem">
                        <div class="nav--system">
                            <router-link
                                :to="systemDetailLinks[0].to"
                                class="nav__link clickable"
                                :class="{
                                    active: isRoutePathActive(systemDetailLinks[0].activePath) && !isRoutePathActive('/systems/(.*)?/invitations'),
                                }"
                            >
                                <span class="nav__link__illustration">
                                    <i class="icon" :class="`icon--${systemDetailLinks[0].icon}`"></i>
                                </span>

                                <span class="nav__link__text">{{ systemDetailLinks[0].label }}</span>
                            </router-link>

                            <ul class="nav__list" v-if="$can('see Extended Navigation', activeSystem)">
                                <li class="nav__item" v-for="(item, index) in systemDetailLinks.slice(1)" :key="index">
                                    <router-link class="nav__link clickable" :to="item.to" :class="{ active: isRoutePathActive(item.activePath) }">
                                        <span class="nav__link__illustration"></span>
                                        <span class="nav__link__text">{{ item.label }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <!-- Users Admin -->
                    <li class="nav__item nav-useradmin" v-if="$can('see', 'User overview')">
                        <router-link to="/users/admin" :class="{ active: isRoutePathActive('/users/admin') }" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-user-group"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('navigation.users_admin') }}</span>
                        </router-link>
                    </li>

                    <!-- User Details -->
                    <li class="nav__item" v-if="activeUser && $can('see', 'User overview')">
                        <div class="nav--system">
                            <router-link
                                :to="userDetailLinks[0].to"
                                class="nav__link nav__link--light clickable"
                                :class="{
                                    active: isRoutePathActive(userDetailLinks[0].activePath),
                                }"
                            >
                                <span class="nav__link__illustration">
                                    <i class="icon" :class="`icon--${userDetailLinks[0].icon}`"></i>
                                </span>

                                <span class="nav__link__text">{{ userDetailLinks[0].label }}</span>
                            </router-link>

                            <ul class="nav__list">
                                <li class="nav__item" v-for="(item, index) in userDetailLinks.slice(1)" :key="index">
                                    <router-link class="nav__link clickable" :to="item.to" :class="{ active: isRoutePathActive(item.activePath) }">
                                        <span class="nav__link__illustration"></span>
                                        <span class="nav__link__text">{{ item.label }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <!-- Organizations -->
                    <li class="nav__item nav-organization" v-if="$can('see', 'Organizations')">
                        <router-link
                            to="/organizations"
                            :class="{
                                active: isRoutePathActive('/organizations') && !isRoutePathActive('/organizations/invitations'),
                            }"
                            class="nav__link clickable"
                        >
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-organizations"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('navigation.organization_overview') }}</span>
                        </router-link>
                    </li>

                    <!-- Organization Details -->
                    <li class="nav__item" v-if="activeOrganization && $can('see', 'Organizations')">
                        <div class="nav--system">
                            <router-link
                                :to="organizationDetailLinks[0].to"
                                class="nav__link nav__link--light clickable"
                                :class="{
                                    active: isRoutePathActive(organizationDetailLinks[0].activePath),
                                }"
                            >
                                <span class="nav__link__illustration">
                                    <i class="icon" :class="`icon--${organizationDetailLinks[0].icon}`"></i>
                                </span>

                                <span class="nav__link__text">{{ organizationDetailLinks[0].label }}</span>
                            </router-link>

                            <ul class="nav__list" v-if="fullActiveOrganization">
                                <template> </template>
                                <li class="nav__item" v-for="(item, index) in organizationDetailLinks.slice(1)" :key="index">
                                    <router-link class="nav__link clickable" :to="item.to" :class="{ active: isRoutePathActive(item.activePath) }">
                                        <span class="nav__link__illustration"></span>
                                        <span class="nav__link__text">{{ item.label }}</span>
                                    </router-link>
                                </li>
                            </ul>
                        </div>
                    </li>

                    <!-- Notification -->
                    <li v-if="$can('manage', 'Notifications')" class="nav__item nav-notifications">
                        <router-link to="/notifications" :class="{ active: isRoutePathActive('/notifications') }" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-notification"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('navigation.notification') }}</span>
                        </router-link>
                    </li>

                    <!-- Certificates -->
                    <li v-if="$can('see', 'Certificates')" class="nav__item nav-certificates">
                        <router-link to="/certificates" :class="{ active: isRoutePathActive('/certificates') }" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--common-certificate"></i>
                            </span>

                            <span class="nav__link__text">{{ $t('navigation.certificates') }}</span>
                        </router-link>
                    </li>

                    <!-- Grafana -->
                    <li v-if="$can('see', 'Grafana')" class="nav__item nav-grafana">
                        <a @click="grafanaLogin" class="nav__link clickable">
                            <span class="nav__link__illustration">
                                <i class="icon icon--grafana"></i>
                            </span>

                            <span class="nav__link__text">Grafana</span>
                        </a>
                    </li>

                    <!-- terms of use -->
                    <li class="nav__item">
                        <a @click="openTerms(false)" class="nav__link nav__link--small clickable">
                            <span class="nav__link__text">{{ $t('navigation.terms_of_use') }}</span>
                        </a>
                    </li>

                    <!-- windhager.com -->
                    <li class="nav__item">
                        <a href="http://www.windhager.com/" target="_blank" class="nav__link nav__link--small">
                            <span class="nav__link__text">Windhager.com</span>
                        </a>
                    </li>

                    <li class="nav__item">
                        <a @click="logout()" class="nav__link nav__link--small clickable">
                            <span class="nav__link__text">Logout</span>
                        </a>
                    </li>
                </ul>
            </nav>

            <div class="page__sidebar__note">Version {{ this.$store.state.version }}</div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapGetters, mapState } from 'vuex';
import openTermsMixin from '@/mixins/openTermsMixin';
import showActivationDialog from '@/mixins/showActivationDialog';
import SlideDownUpTransition from '@/components/SlideDownUpTransition';
import InvitationCheck from '@/helpers/invitations-check';
import eventBus from '@/utils/eventBus';
import routePathActive from '@/mixins/routePathActive';
import generalLinks from '@/mixins/generalLinks';

export default {
    name: 'Sidebar',
    mixins: [openTermsMixin, showActivationDialog, routePathActive, generalLinks],
    components: { SlideDownUpTransition },
    data() {
        return {
            showInvitations: false,
            languageOpen: false,
            verifyOpen: false,
            mobileNavOpen: false,
        };
    },
    computed: {
        ...mapGetters(['hasMinPro', 'activeOrganization', 'fullActiveOrganization', 'systemUserPermission', 'userData', 'activeSystem', 'activeUser']),
        ...mapState(['userData', 'invitations']),
    },
    watch: {
        $route() {
            this.mobileNavOpen = false;
        },
        mobileNavOpen(newVal) {
            this.$store.commit('setLocked', newVal);
        },
    },
    created() {
        this.addListeners();
        this.$store.dispatch('loadUserData');
        InvitationCheck.start();
    },
    beforeDestroy() {
        this.removeListeners();
    },
    methods: {
        async grafanaLogin() {
            try {
                const response = await api.users.grafana();
                window.open(response.data.login_url, '_blank').focus();
            } catch (error) {
                console.error(error);
            }
        },

        checkTermsOfUseVersionHandler(data) {
            if (data.pendingVersion && data.pendingVersion.version && data.acceptedVersion !== data.pendingVersion.version) {
                this.openTerms(data.pendingVersion.urls[this.userData.locale], true);
            }

            if (this.userData.email_confirmed === false && this.verifyOpen === false) {
                this.verifyOpen = true;
                this.showVerification();
            }
        },
        showVerification() {
            this.$modal.show(
                () => import('@/views/modal/VerifyUser.vue'),
                {
                    successCallback: () => {
                        console.log('success');
                    },
                },
                {
                    name: 'VerifyUser',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                    clickToClose: false,
                }
            );
        },
        logout() {
            this.$store.dispatch('logout').then(() => {
                InvitationCheck.stop();
                InvitationCheck.clear();

                this.$router.push('/');
            });
        },
        addListeners() {
            eventBus.$on('checkTermsOfUseVersion', this.checkTermsOfUseVersionHandler);
        },
        removeListeners() {
            eventBus.$off('checkTermsOfUseVersion', this.checkTermsOfUseVersionHandler);
        },
    },
};
</script>

<style lang="scss">
$block: 'nav';

// Base Nav
.#{$block} {
    &__list {
        padding: 0;
        margin: 0;
        list-style-type: none;
    }

    &__item {
        display: block;
    }

    &__link {
        display: block;
        padding: 15px 25px;
        overflow: hidden;
        position: relative;

        font-weight: normal;
        white-space: nowrap;
        text-overflow: ellipsis;

        &--badgy {
            padding-right: 50px;
        }

        &--small {
            padding: 14px 10px 14px 20px;
            font-size: $font-small-size;
        }

        &__illustration {
            width: 50px;
            display: inline-block;
            vertical-align: middle;

            text-align: center;

            .icon {
                display: inline-block;
                line-height: 0;
                vertical-align: middle;
                font-size: 25px;

                &.icon--common-systems {
                    font-size: 19px;
                }
            }

            &__image {
                display: block;
                margin: 0 auto;
                border-radius: 50%;
            }
        }

        &__text {
            vertical-align: middle;
        }
    }

    &__badge {
        position: absolute;
        right: 20px;
        top: 50%;
        border: 1px solid currentColor;
        width: 22px;
        height: 22px;
        line-height: 22px;
        text-align: center;
        border-radius: 50%;
        font-size: 0.75em;
        margin-top: -11px;
    }

    &__sub {
        padding: 0;
        margin: 0;
        display: none;

        .active + & {
            display: block;
        }

        list-style-type: none;
        background-color: $color-gray-dark;
        font-size: $font-small-size;

        &__item {
            display: block;
            border-bottom: 1px solid $color-gray;
        }

        &__link {
            display: block;
            padding: 12px 0 12px 54px;

            color: $color-gray-light;
            font-weight: normal;

            &.active {
                color: $color-accent;
            }

            @media (hover: hover) {
                &:hover {
                    color: $color-accent;
                }
            }

            &--small {
                padding-left: 30px;
            }
        }
    }
}

// Primary Nav
.#{$block}--primary {
    border-top: 1px solid $color-gray;

    @include mq(wrapper) {
        width: $sidebar-width;
    }

    .#{$block} {
        &__link {
            padding-left: 0;
            color: $color-gray-light;
            border-bottom: 1px solid $color-gray-dark;

            &.active {
                background-color: $color-gray-dark;
                border-bottom-color: $color-gray;
                color: $color-accent;
            }

            @media (hover: hover) {
                &:hover {
                    background-color: $color-gray-dark;
                    border-bottom-color: $color-gray;
                    color: $color-accent;
                }
            }

            &--small {
                padding-left: 20px;
            }
        }
    }
}

// Secondary Nav
.#{$block}--secondary {
    .#{$block} {
        &__link {
            border-bottom: 1px solid #bbb;
            color: #fff;

            &.active {
                background-color: $color-gray-dark;
            }

            @media (hover: hover) {
                &:hover {
                    background-color: $color-gray-dark;
                }
            }

            &:after {
                content: '›';
                float: right;
            }
        }
    }
}

// Tertiary Nav
.#{$block}--tertiary {
    .#{$block} {
        &__link {
            color: #414141;
            background: #fff;

            .icon {
                font-size: 1.6em;
            }

            &.active {
                .icon {
                    color: $color-success;
                }
            }
        }

        &__item {
            &:nth-child(odd) {
                .#{$block}__link {
                    background: #efefef;
                }
            }
        }
    }
}

// System Nav
.#{$block}--system {
    .#{$block} {
        &__link {
            background-color: $color-gray-dark;
            border-bottom-color: $color-gray;

            &.active {
                color: $color-accent;
            }

            @media (hover: hover) {
                &:hover {
                    color: $color-accent;
                }
            }

            &.inactive {
                color: $color-gray;
                opacity: 1;
            }

            &--light {
                background-color: transparent;
                border-bottom-color: $color-gray-dark;
            }
        }
    }
}

// Invitations Nav
.#{$block}--invitations {
    .#{$block} {
        &__list {
            // display: none;
            //
            // &.active {
            //     display: block;
            // }

            .#{$block}__link {
                padding-left: 15px;
                background-color: $color-gray-dark;
                border-bottom-color: $color-gray;
                white-space: normal;
            }
        }
    }
}
</style>
